import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  cn,
} from '@sr/ui-library';
import {
  TASK_SORT_FIELDS,
  TASK_SORT_ORDER_LABELS,
  type TaskSortField,
  type TaskSortOrder,
  type TaskSortSelection,
} from './taskSort';

type SortMenuProps<T extends string> = {
  disabled: boolean;
  /** Read before the selected option, so the accessible name includes the visible text. */
  label: string;
  onValueChange: (value: T) => void;
  options: { label: string; value: T }[];
  value: T;
};

const SortMenu = <T extends string>({
  disabled,
  label,
  onValueChange,
  options,
  value,
}: SortMenuProps<T>) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        {/*
         * `transparent`, not `ghost`: inside `.ui-button` the accent tokens are
         * brand red, so a ghost hover turns the label red.
         */}
        <Button
          className="min-w-0 px-0 font-normal text-muted-foreground"
          disabled={disabled}
          sectionRight={<Icon icon="chevronDown" />}
          size="sm"
          variant="transparent"
        >
          <span className="sr-only">{label} </span>
          {options.find((option) => option.value === value)?.label}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="rounded-lg p-2">
        {/*
         * Plain items with radio semantics: `DropdownMenuRadioItem` draws a dot
         * on the checked row and reserves a left gutter for it on every row,
         * while the design marks the choice by colour alone.
         */}
        {options.map((option) => {
          const isSelected = option.value === value;

          return (
            <DropdownMenuItem
              key={option.value}
              aria-checked={isSelected}
              className={cn(
                'cursor-pointer px-3 py-2',
                isSelected &&
                  'bg-primary/10 text-primary focus:bg-primary/10 focus:text-primary',
              )}
              role="menuitemradio"
              onSelect={() => onValueChange(option.value)}
            >
              {option.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ORDER_OPTIONS = (
  Object.entries(TASK_SORT_ORDER_LABELS) as [TaskSortOrder, string][]
).map(([value, label]) => ({ label, value }));

type TasksSortControlsProps = {
  disabled: boolean;
  fields: TaskSortField[];
  onSelectionChange: (selection: TaskSortSelection) => void;
  selection: TaskSortSelection;
};

/** Field and direction dropdowns for a Tasks-page tab. */
export const TasksSortControls = ({
  disabled,
  fields,
  onSelectionChange,
  selection,
}: TasksSortControlsProps) => (
  <div className="flex items-center gap-4">
    <SortMenu
      label="Sort by"
      disabled={disabled}
      options={fields.map((field) => ({
        label: TASK_SORT_FIELDS[field].label,
        value: field,
      }))}
      value={selection.field}
      onValueChange={(field) =>
        onSelectionChange({
          field,
          // Re-picking the current field (e.g. to close the menu) keeps its direction.
          order:
            field === selection.field
              ? selection.order
              : TASK_SORT_FIELDS[field].defaultOrder,
        })
      }
    />

    <SortMenu
      label="Sort direction"
      disabled={disabled}
      options={ORDER_OPTIONS}
      value={selection.order}
      onValueChange={(order) => onSelectionChange({ ...selection, order })}
    />
  </div>
);
